import { DocumentationReferenceNumberField } from "@/services/backend/vbs/work-orders/components/documentation-reference-number-field";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/shared/components/ui/card";
import t from "@/lang/lang";
import { AddItemButton } from "@/services/backend/vbs/work-orders/components/add-item-button";
import { useVbsDocumentationShowQuery } from "@/services/backend/vbs/work-orders/api/documentation-api";
import { RTKQueryErrorAlert } from "@/shared/components/errors/rtk-query-error-alert";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/shared/components/ui/table";
import { DeleteItemButton } from "@/services/backend/vbs/work-orders/components/delete-item-button";
import { EditItemDialog } from "@/services/backend/vbs/work-orders/components/edit-item-dialog";
import {
  useFieldErrors,
  ErrValidationFailedContextProvider,
} from "@/shared/lib/errors/err-validation-failed-context";
import { FieldErrors } from "@/shared/components/field/field";

export function DocumentationCard({
  documentationId,
  workOrderId,
  disabled = false,
}: {
  documentationId: string;
  workOrderId: string;
  disabled?: boolean;
}) {
  return (
    <Card>
      <CardHeader className="pb-2">
        <div className="flex justify-between">
          <div>
            <CardTitle>{t("Dokumentation")}</CardTitle>
            <CardDescription className="max-w-4xl">
              {t(
                "Hier besteht die Möglichkeit die Arbeiten und die Arbeitsbedingungen zu dokumentieren.",
              )}
            </CardDescription>
          </div>
          <AddItemButton
            documentationId={documentationId!}
            disabled={disabled}
          />
        </div>
      </CardHeader>
      <CardContent className="grid gap-4">
        <DocumentationFieldErrors />
        {/*
          The documentation is a document of its own, rendered inside the page
          of its work order. Its errors get a scope of their own, so they do
          not collide with the work order's "referenceNumber".
        */}
        <ErrValidationFailedContextProvider>
          <DocumentationReferenceNumberField
            documentationId={documentationId}
            disabled={disabled}
          />
        </ErrValidationFailedContextProvider>
        <DocumentationTable
          documentationId={documentationId!}
          workOrderId={workOrderId}
          disabled={disabled}
        />
      </CardContent>
    </Card>
  );
}

// DocumentationFieldErrors shows the error the readiness check of the work
// order reports for its documentation. Adding, editing and deleting items
// reset it.
function DocumentationFieldErrors() {
  const { errors } = useFieldErrors("documentation");
  return <FieldErrors errors={errors} />;
}

function DocumentationTable({
  documentationId,
  workOrderId,
  disabled,
}: {
  documentationId: string;
  workOrderId: string;
  disabled: boolean;
}) {
  const {
    data: documentation,
    isLoading,
    error,
  } = useVbsDocumentationShowQuery({
    id: documentationId,
  });

  if (error) {
    return <RTKQueryErrorAlert error={error} />;
  }

  if (isLoading) {
    return <TableSkeleton />;
  }

  const documentationItems = documentation!.items;

  return (
    <Table>
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nr.")}</TableHead>
          <TableHead className="w-full">{t("Titel")}</TableHead>
          <TableHead className="whitespace-nowrap">{t("Intern")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
      <TableBody>
        {documentationItems.map((item) => (
          <TableRow key={item.id}>
            <TableCell className="text-center">{item.number}</TableCell>
            <TableCell>{item.title}</TableCell>
            <TableCell>{item.isInternal ? t("Ja") : t("Nein")}</TableCell>
            <TableCell className="flex items-center space-x-2">
              <EditItemDialog
                documentationId={documentationId}
                itemId={item.id}
                workOrderId={workOrderId}
                disabled={disabled}
              />
              <DeleteItemButton
                documentationId={documentationId}
                itemId={item.id}
                disabled={disabled}
              />
            </TableCell>
          </TableRow>
        ))}
      </TableBody>
    </Table>
  );
}

function TableSkeleton() {
  return (
    <Table className="animate-pulse">
      <TableHeader>
        <TableRow>
          <TableHead>{t("Nr.")}</TableHead>
          <TableHead>{t("Position")}</TableHead>
          <TableHead className="w-full">{t("Titel")}</TableHead>
          <TableHead className="whitespace-nowrap">{t("Intern")}</TableHead>
          <TableHead>{t("Aktionen")}</TableHead>
        </TableRow>
      </TableHeader>
    </Table>
  );
}
